import { useState, type MouseEvent, type ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'
import { PLAN_PRICES } from '@constants/pricingPlans'
import { AnnualSavingsLine } from './AnnualSavingsLine'
import { BillingPeriodToggle } from './BillingPeriodToggle'
import { PlanFeatureList } from './PlanFeatureList'

interface PricingPlansSectionProps {
  readonly showTrialBanner?: boolean
  readonly showSignupCtas?: boolean
  readonly showSectionHeading?: boolean
  readonly compactSpacing?: boolean
  readonly onSignupClick?: (e: MouseEvent<HTMLElement>) => void
  readonly belowPlans?: ReactNode
  readonly className?: string
  readonly id?: string
}

export function PricingPlansSection({
  showTrialBanner = true,
  showSignupCtas = true,
  showSectionHeading = true,
  compactSpacing = false,
  onSignupClick,
  belowPlans,
  className = 'bg-secondary-100 px-5 py-20 sm:px-8 sm:py-24',
  id = 'precos',
}: PricingPlansSectionProps) {
  const [annual, setAnnual] = useState(false)
  const prices = annual ? PLAN_PRICES.annual : PLAN_PRICES.monthly
  const { base: basePrice, proUpgrade: proUpgradeDelta } = prices

  return (
    <section id={id} className={className}>
      <div className="mx-auto max-w-6xl overflow-x-hidden">
        {showSectionHeading && (
          <>
            <div className="mb-4 text-xs font-medium uppercase tracking-widest text-primary-600">
              Preços
            </div>
            <h2 className="mb-4 text-3xl font-bold text-secondary-900 sm:text-4xl">
              Simples como deve ser
            </h2>
            <p className="mb-8 text-secondary-600">
              Dois planos. Preços claros. Sem surpresas no fim do mês.
            </p>
          </>
        )}

        {showTrialBanner && (
          <div className="mb-8 flex flex-wrap items-center gap-4 rounded-2xl border-2 border-primary-200 bg-gradient-to-br from-primary-50 to-primary-100 p-5 sm:p-6">
            <span className="text-3xl" aria-hidden>
              🎁
            </span>
            <div className="min-w-0 flex-1 basis-[12rem]">
              <strong className="block text-base text-primary-900">Comece grátis já hoje. Sem cartão de crédito</strong>
              <span className="mt-1 block text-sm text-primary-800">
                30 dias completos para testar em ambiente real. Se não gostar, não paga nada.
              </span>
            </div>
            <span className="shrink-0 rounded-full bg-primary-600 px-4 py-1.5 text-sm font-bold text-white">
              30 dias grátis
            </span>
          </div>
        )}

        <div
          className={
            compactSpacing
              ? 'mb-2 pb-0'
              : 'mb-10 border-b border-secondary-100 pb-10 sm:mb-12 sm:pb-12'
          }
        >
          <BillingPeriodToggle annual={annual} onToggle={() => setAnnual((v) => !v)} />
        </div>

        <div className="relative z-0 grid grid-cols-1 items-stretch gap-8 md:grid-cols-2 md:gap-8">
          <div className="flex h-full min-w-0 flex-col rounded-2xl border border-secondary-200 bg-white p-6 shadow-sm sm:p-8">
            <span className="mb-3 inline-flex w-fit rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
              Básico
            </span>
            <h3 className="text-xl font-bold text-secondary-900">FIXDAUTO Base</h3>
            <p className="mt-2 text-sm leading-relaxed text-secondary-600">
              Gestão completa da oficina num só lugar. Deixa de perder tempo com papéis e passa a ter tudo organizado e
              acessível em segundos.
            </p>
            {showTrialBanner && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-2.5 text-sm font-semibold text-primary-800">
                <span className="text-primary-600" aria-hidden>
                  ✓
                </span>
                30 dias grátis — sem cartão de crédito
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-baseline gap-0.5">
              <span className="text-4xl font-extrabold tracking-tight text-secondary-900 sm:text-5xl">{basePrice}</span>
              <span className="text-xl font-bold text-primary-600">€</span>
              <span className="text-sm text-secondary-500">/mês</span>
            </div>
            <AnnualSavingsLine annual={annual} className="text-primary-600">
              290€/ano (poupa 58€)
            </AnnualSavingsLine>
            <hr className="my-5 border-secondary-100" />
            <div className="flex flex-1 flex-col">
              <PlanFeatureList variant="base" />
            </div>
            {showSignupCtas && onSignupClick ? (
              <>
                <a
                  href="#registo"
                  onClick={onSignupClick}
                  className="btn btn-primary mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold no-underline"
                >
                  Testar grátis durante 30 dias
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden strokeWidth={2.25} />
                </a>
                <p className="mt-2 text-center text-xs text-secondary-500">
                  Sem cartão de crédito. Cancela quando quiser.
                </p>
              </>
            ) : (
              <div className="mt-2 h-1 shrink-0" aria-hidden />
            )}
          </div>

          <div className="relative isolate flex h-full min-w-0 flex-col overflow-hidden rounded-2xl bg-secondary-800 p-6 shadow-xl ring-2 ring-primary-500/40 sm:p-8">
            <div
              className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
              aria-hidden
            >
              <div className="absolute -right-12 top-0 h-40 w-40 rounded-full bg-primary-500/20 blur-2xl sm:-right-16 sm:h-48 sm:w-48" />
            </div>
            <div className="relative flex h-full min-w-0 flex-col">
              <span className="mb-3 inline-flex w-fit rounded-full bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-200">
                ⭐ Recomendado
              </span>
              <h3 className="text-xl font-bold text-white">FIXDAUTO Pro</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">
                Tudo do Base, mais a IA que distingue o FIXDAUTO de qualquer outro software. É a diferença entre gerir a
                oficina e fazer a oficina crescer.
              </p>
              {showTrialBanner && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary-300/40 bg-primary-400/15 px-3.5 py-2.5 text-sm font-semibold text-primary-200">
                  <span aria-hidden>✓</span>
                  30 dias grátis — sem cartão de crédito
                </div>
              )}
              <div className="mt-5 flex flex-wrap items-baseline gap-0.5">
                <span className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">+{proUpgradeDelta}</span>
                <span className="text-xl font-bold text-primary-300">€</span>
                <span className="text-sm text-white/50">/mês</span>
              </div>
              <AnnualSavingsLine annual={annual} className="text-primary-300">
                650€/ano (poupa 130€)
              </AnnualSavingsLine>
              <hr className="my-5 border-white/10" />
              <div className="flex flex-1 flex-col">
                <PlanFeatureList variant="pro" dark />
              </div>
              {showSignupCtas && onSignupClick ? (
                <>
                  <a
                    href="#registo"
                    onClick={onSignupClick}
                    className="btn btn-primary mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold no-underline"
                  >
                    Testar grátis durante 30 dias
                    <ArrowRight className="h-4 w-4 shrink-0" aria-hidden strokeWidth={2.25} />
                  </a>
                  <p className="mt-2 text-center text-xs text-white/40">Sem cartão de crédito. Cancela quando quiser.</p>
                </>
              ) : (
                <div className="mt-2 h-1 shrink-0" aria-hidden />
              )}
            </div>
          </div>
        </div>

        {belowPlans && (
          <div
            className={
              compactSpacing
                ? 'relative z-10 mt-6 flex justify-center pt-2'
                : 'relative z-10 mt-12 flex justify-center border-t border-secondary-100 pt-10 sm:mt-14 sm:pt-12'
            }
          >
            {belowPlans}
          </div>
        )}
      </div>
    </section>
  )
}
